<template>
  <div class="row">
    <div v-if="showEditForm" class="col-12">
      <create-heavy-machine-form
        :edit-mode="true"
        :name-prop="toolGroup.name"
        :licence-plate-prop="licencePlate"
        :tool-group-image-bytes-prop="toolGroup.toolGroupImageBytes || ''"
        :machine-id-prop="String(toolGroupId)"
        @close="showEditForm = false"
        @saved="onMachineSaved"
      ></create-heavy-machine-form>
    </div>
    <div class="col-md-4">
      <card>
        <h2>{{ toolGroup.name }}</h2>
        <img v-if="toolGroup.toolGroupImageBytes" class="img-fluid shadow border-radius-lg" :src="'data:image/png;base64,' + toolGroup.toolGroupImageBytes" alt="machine image" />
        <h3 v-if="licencePlate">{{ licencePlate }}</h3>
        <h4 v-if="assignedTo"><b>{{$t('heavyMachines.assignedTo')}}:</b> {{ assignedTo }}</h4>
        <h4 v-if="nextInspection"><b>{{$t('heavyMachines.nextInspection')}}:</b> {{ nextInspection }}</h4>
        <h6 v-if="enableGPS && toolGroup.deviceId">
          <b>{{ $t('tools.gpsDevice') }}:</b>
          {{ toolGroup.deviceTypeName }} {{ toolGroup.deviceId }}
          <span v-if="toolGroup.deviceBatteryPct != null">
            <i class="fas fa-battery-full ms-2 me-1" :style="{ color: batteryColor }"></i>
            {{ Math.round(toolGroup.deviceBatteryPct) }}%
          </span>
          <device-status-badge class="ms-2" :last-contact="toolGroup.deviceLastContact" />
        </h6>
        <div class="row">
          <div v-if="canEditMachine" class="col-md-12">
            <ul class="navbar-nav d-flex justify-content-end">
              <li class="nav-item dropdown w-100">
                <a class="nav-link dropdown-toggle d-flex align-items-center justify-content-center py-2 btn mb-0 btn-dark btn-block" href="#" role="button"
                  data-bs-toggle="dropdown" aria-expanded="false">
                  {{ $t('common.actions') }}
                </a>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li v-if="isAdmin && !isMobile">
                    <a class="dropdown-item black d-flex align-items-center" @click="printQRCode">
                      <i class="material-icons black me-2">cloud_download</i>
                      {{ $t('tools.printQR') }}
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item black d-flex align-items-center" @click="showInspectionModal = true">
                      <i class="material-icons black me-2">event_available</i>
                      {{ $t('heavyMachines.registerInspection') }}
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item black d-flex align-items-center" @click="showLastServiceModal = true">
                      <i class="material-icons black me-2">edit</i>
                      {{ $t('heavyMachines.setServiceDetails') }}
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item black d-flex align-items-center" @click="openEditForm">
                      <i class="material-icons black me-2">tune</i>
                      {{ $t('heavyMachines.editHeavyMachine') }}
                    </a>
                  </li>
                  <li v-if="isAdmin && assignedTo">
                    <a class="dropdown-item red d-flex align-items-center" @click="unreserve">
                      <i class="material-icons red me-2">clear</i>
                      {{ $t('heavyMachines.unassign') }}
                    </a>
                  </li>
                  <li v-if="isAdmin && !assignedTo && !showReserveTable">
                    <a class="dropdown-item green d-flex align-items-center" @click="showReserveTable = true">
                      <i class="material-icons green me-2">person</i>
                      {{ $t('heavyMachines.assignToUser') }}
                    </a>
                  </li>
                  <li v-if="isAdmin && enableGPS">
                    <a class="dropdown-item black d-flex align-items-center" @click="openGpsDeviceModal">
                      <i class="material-icons black me-2">sensors</i>
                      {{ $t('heavyMachines.manageGpsDevice') }}
                    </a>
                  </li>
                  <li v-if="isAdmin && enableGPS && toolGroup.deviceId">
                    <a class="dropdown-item black d-flex align-items-center" @click="goToGeoTraces">
                      <i class="material-icons black me-2">place</i>
                      {{ $t('tools.viewGeoTraces') }}
                    </a>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </card>

      <modal v-model:show="showInspectionModal" class="white-content">
        <h3 class="card-title">{{$t('heavyMachines.registerInspection')}}</h3>
        <div class="row">
          <div class="col-12">
            <label class="col-form-label">{{$t('heavyMachines.nextInspection')}}</label>
            <base-input>
              <el-date-picker
                v-model="inspectionDate"
                type="date"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DD"
                :placeholder="$t('heavyMachines.nextInspection')"
              >
              </el-date-picker>
            </base-input>
            <material-input-field
              id="inspectionComment"
              v-model="inspectionComment"
              :label="$t('heavyMachines.inspectionComment')"
              :placeholder="$t('heavyMachines.inspectionComment')"
              name="inspectionComment"
              variant="static"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-sm d-flex justify-content-end">
            <material-button color="dark" type="standard" fill @click="saveInspection">{{$t('common.submit')}}</material-button>
          </div>
        </div>
      </modal>

      <modal v-model:show="showLastServiceModal" class="white-content">
        <h3 class="card-title">{{$t('heavyMachines.setServiceDetails')}}</h3>
        <div class="row">
          <div class="col-12">
            <label class="col-form-label">{{$t('heavyMachines.lastService')}}</label>
            <base-input>
              <el-date-picker
                v-model="lastService"
                type="date"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DD"
                :placeholder="$t('heavyMachines.lastService')"
              >
              </el-date-picker>
            </base-input>
            <material-input-field
              id="comment"
              v-model="comment"
              :label="`${$t('heavyMachines.comment')} *`"
              :placeholder="$t('heavyMachines.comment')"
              name="comment"
              variant="static"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-sm d-flex justify-content-end">
            <material-button color="dark" type="standard" fill @click="saveLastServiceDetails">{{$t('common.submit')}}</material-button>
          </div>
        </div>
      </modal>

      <modal v-model:show="showGpsDeviceModal" class="white-content">
        <h3 class="card-title">{{ $t('heavyMachines.manageGpsDevice') }}</h3>
        <div class="row">
          <div class="col-12 mb-3">
            <material-input-field
              id="machineDeviceId"
              v-model="deviceIdInput"
              :label="$t('tools.deviceId')"
              :placeholder="$t('tools.deviceId')"
              name="machineDeviceId"
              variant="static"
            />
          </div>
          <div class="col-12 mb-3">
            <label>{{ $t('tools.deviceType') }}</label>
            <el-select
              v-model="deviceTypeOption"
              filterable
              clearable
              size="large"
              :placeholder="$t('tools.selectDeviceType')"
              class="w-100"
            >
              <el-option
                v-for="item in deviceTypeOptions"
                :key="item.id"
                :value="item.id"
                :label="item.type"
              />
            </el-select>
          </div>
        </div>
        <div class="row">
          <div class="col-sm d-flex justify-content-end">
            <material-button color="dark" type="standard" fill @click="saveGpsDevice">
              {{ $t('common.submit') }}
            </material-button>
          </div>
        </div>
      </modal>

      <div class="row">
        <user-reserve-table
          v-if="showReserveTable"
          :tool-group-id="toolGroupId"
          :success-message="$t('heavyMachines.machineAssigned')"
          @close="showReserveTable = false"
          @refresh-data="fetchData"
        ></user-reserve-table>
      </div>
    </div>

    <div class="col-md-8">
      <card :title="historyCardTitle" class="stretch-card">
        <template #table-select>
          <NavPills
            v-model="activeHistoryIndex"
            :items="historyNavPillItems"
          />
        </template>
        <div class="table-responsive">
          <el-table v-if="activeHistoryIndex === 0" :data="inspectionHistory">
            <el-table-column :min-width="120" prop="date" :label="$t('heavyMachines.nextInspection')"></el-table-column>
            <el-table-column :min-width="200" prop="comment" :label="$t('heavyMachines.inspectionComment')"></el-table-column>
          </el-table>
          <el-table v-else :data="serviceHistory">
            <el-table-column :min-width="120" prop="date" :label="$t('common.date')"></el-table-column>
            <el-table-column :min-width="200" prop="comment" :label="$t('heavyMachines.comment')"></el-table-column>
          </el-table>
        </div>
        <div v-show="false" class="row">
          <card type="toolqr">
            <div class="qr-print-container">
              <div>
                <img src=img/sidebar-header-tolspor2.png alt="app-logo" class="print-logo"/>
              </div>
              <qrcode-vue
                ref="printqrcode"
                render-as="svg"
                :value="qrValue"
                :size="qrPrintSize"
                level="H"
                background="#ffffff"
                foreground="#000000"
              >
              </qrcode-vue>
              <h3>{{ toolGroup.name }}</h3>
            </div>
          </card>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import UserReserveTable from "../Cars/UserReserveTable";
import CreateHeavyMachineForm from "./CreateHeavyMachineForm";
import { ElTable, ElTableColumn, ElDatePicker, ElSelect, ElOption } from 'element-plus';
import { getCustomerConfig } from '../Settings/customerConfigHelper';
import { jwtDecode } from "jwt-decode";
import Cookies from 'js-cookie';
import swal from 'sweetalert2';
import config from "@/config";
import QrcodeVue from 'qrcode.vue';
import Modal from '../../components/Modals/Modal.vue';
import Card from "../../components/Card.vue";
import BaseInput from "../../components/BaseInput.vue";
import MaterialButton from "../../components/MaterialButton.vue";
import MaterialInputField from "../../components/MaterialInputField.vue";
import NavPills from "../../components/NavPills.vue";
import DeviceStatusBadge from "../../components/DeviceStatusBadge.vue";
import { INSPECTION_PROPERTY_PREFIX, inspectionsFromProperties, formatInspectionDate } from './heavyMachineHelper';

export default {
  components: {
    QrcodeVue,
    UserReserveTable,
    CreateHeavyMachineForm,
    ElTable,
    ElTableColumn,
    ElDatePicker,
    ElSelect,
    ElOption,
    Modal,
    Card,
    BaseInput,
    MaterialButton,
    MaterialInputField,
    NavPills,
    DeviceStatusBadge
  },
  data() {
    return {
      qrPrintSize: 256,
      toolGroup: {},
      toolGroupId: 0,
      assignedTo: '',
      licencePlate: '',
      nextInspection: '',
      inspectionDate: '',
      inspectionComment: '',
      inspectionHistory: [],
      lastService: '',
      comment: '',
      serviceHistory: [],
      showInspectionModal: false,
      showLastServiceModal: false,
      showGpsDeviceModal: false,
      activeHistoryIndex: 0,
      showReserveTable: false,
      enableGPS: false,
      deviceIdInput: '',
      deviceTypeOption: null,
      deviceTypeOptions: [],
      isMobile: window.innerWidth <= 1024,
      userRole: '',
      currentUserId: '',
      allowAssignedUsersToEditVehicles: false,
      showEditForm: false
    };
  },
  computed: {
    isAdmin() {
      return this.userRole !== 'User';
    },
    // Users may register inspection/service and edit a machine assigned to them when the customer allows it
    canEditMachine() {
      if (this.isAdmin) return true;
      return this.allowAssignedUsersToEditVehicles && (this.toolGroup.toolGroupReservations || [])
        .some(r => r.isReserved && !r.externalUser && r.user && r.user.id === this.currentUserId);
    },
    batteryColor() {
      const pct = this.toolGroup?.deviceBatteryPct;
      if (pct == null) return '';
      if (pct <= 10) return '#dc3545';
      if (pct <= 39) return '#ffc107';
      return '#28a745';
    },
    qrValue() {
      return `${config.baseUrl}/heavymachine/${this.toolGroup.id}`;
    },
    historyNavPillItems() {
      return [
        { label: this.$t('heavyMachines.inspectionHistory'), icon: 'fact_check' },
        { label: this.$t('heavyMachines.serviceHistory'), icon: 'notes' }
      ];
    },
    historyCardTitle() {
      return this.historyNavPillItems[this.activeHistoryIndex].label;
    }
  },
  created() {
    this.toolGroupId = this.$route.params.toolGroupId
    const token = Cookies.get('accessToken');
    const decodedToken = jwtDecode(token);
    this.userRole = decodedToken.role
    this.currentUserId = decodedToken.UserIdentifier
    this.fetchData();
    getCustomerConfig('EnableGPS').then(config => {
      this.enableGPS = config === true;
    });
    getCustomerConfig('AllowAssignedUsersToEditVehicles').then(config => {
      this.allowAssignedUsersToEditVehicles = config === true;
    });
    window.addEventListener('resize', this.updateIsMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateIsMobile);
  },
  methods: {
    updateIsMobile() {
      this.isMobile = window.innerWidth <= 1024;
    },
    openEditForm() {
      this.showEditForm = true;
      this.$nextTick(() => {
        window.scrollTo(0, 0);
      });
    },
    onMachineSaved() {
      this.showEditForm = false;
      this.fetchData();
    },
    generateGUID() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    },
    async saveInspection() {
      if (!this.inspectionDate) {
        this.$toast.info(this.$t('heavyMachines.nextInspectionRequired'), { timeout: 2500 });
        return;
      }
      try {
        await api.put(`/ToolGroup/${this.toolGroupId}`, {
          properties: [{ key: INSPECTION_PROPERTY_PREFIX + this.generateGUID(), value: this.inspectionDate + '|' + this.inspectionComment }]
        });
        this.$toast.success(this.$t('common.updated'));
        this.showInspectionModal = false;
        this.inspectionDate = '';
        this.inspectionComment = '';
        this.fetchData();
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
        this.showInspectionModal = false;
      }
    },
    async saveLastServiceDetails() {
      try {
        await api.put(`/ToolGroup/${this.toolGroupId}`, {
          properties: [{ key: 'carService' + this.generateGUID(), value: this.lastService + '|' + this.comment }]
        });
        this.$toast.success(this.$t('common.updated'));
        this.showLastServiceModal = false;
        this.lastService = '';
        this.comment = '';
        this.fetchData();
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
        this.showLastServiceModal = false;
      }
    },
    fetchData() {
      api.get(`/ToolGroup/${this.toolGroupId}`)
        .then(response => {
          this.toolGroup = response.data;
          const properties = this.toolGroup.toolGroupProperties || [];

          const activeReservation = (this.toolGroup.toolGroupReservations || []).find(p => p.isReserved === true);
          this.assignedTo = activeReservation ? `${activeReservation.user.firstName} ${activeReservation.user.lastName}` : '';

          const licencePlateProperty = properties.find(p => p.key === 'licencePlate');
          this.licencePlate = licencePlateProperty ? licencePlateProperty.value : '';

          const inspections = inspectionsFromProperties(properties);
          this.nextInspection = inspections.length > 0 ? formatInspectionDate(inspections[0].date) : '';
          this.inspectionHistory = inspections.map(i => ({ date: formatInspectionDate(i.date), comment: i.comment }));

          this.serviceHistory = properties
            .filter(p => p.key.startsWith('carService'))
            .map(p => {
              const [date, comment] = p.value.split('|');
              return { date, comment };
            })
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map(item => ({ date: formatInspectionDate(item.date), comment: item.comment }));
        })
        .catch(error => {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
        });
    },
    async unreserve() {
      const result = await swal.fire({
        title: this.$t('heavyMachines.areYouSureUnassign'),
        icon: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'btn btn-success btn-fill',
          cancelButton: 'btn btn-danger btn-fill'
        },
        confirmButtonText: this.$t('heavyMachines.yesUnassignIt'),
        cancelButtonText: this.$t('delete.cancel'),
        buttonsStyling: false
      });
      if (result.isConfirmed) {
        try {
          await api.put(`/ToolGroup/Unreserve/${this.toolGroupId}`);
          this.fetchData();
          this.$toast.success(this.$t('common.updated'), { timeout: 1500 });
        } catch (error) {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
        }
      }
    },
    async openGpsDeviceModal() {
      this.deviceIdInput = this.toolGroup.deviceId || '';
      this.deviceTypeOption = this.toolGroup.deviceTypeId || null;
      if (this.deviceTypeOptions.length === 0) {
        try {
          const response = await api.get('/DeviceType');
          this.deviceTypeOptions = response.data;
        } catch (error) {
          console.error('API request error:', error); // eslint-disable-line no-console
          this.$toast.error(this.$t('common.unableToRetrieveData'), { timeout: 0 });
          return;
        }
      }
      this.showGpsDeviceModal = true;
    },
    async saveGpsDevice() {
      const trimmedId = (this.deviceIdInput || '').trim();
      if (trimmedId !== '' && this.deviceTypeOption == null) {
        this.$toast.info(this.$t('tools.selectDeviceType'), { timeout: 2000 });
        return;
      }
      try {
        await api.put(`/ToolGroup/${this.toolGroupId}`, {
          updateDevice: true,
          deviceId: trimmedId === '' ? null : trimmedId,
          deviceTypeId: trimmedId === '' ? null : this.deviceTypeOption,
        });
        this.$toast.success(this.$t('common.updated'), { timeout: 1500 });
        this.showGpsDeviceModal = false;
        this.fetchData();
      } catch (error) {
        console.error('API request error:', error); // eslint-disable-line no-console
        this.$toast.error(this.$t('common.unableToUpdate'), { timeout: 0 });
      }
    },
    goToGeoTraces() {
      this.$router.push({ name: 'Heavy Machine GeoTraces', params: { toolGroupId: this.toolGroupId } });
    },
    printQRCode() {
      if (this.isMobile) {
        return;
      }

      const pageWidth = '3in';
      const pageHeight = '2in';
      const qrSize = '1.7in';
      const logoWidth = '1.5in';
      const logoHeight = '0.4in';
      const fontSize = '0.15in';
      const padding = '0.03in';
      const logoMarginBottom = '0.03in';

      const truncatedName = this.toolGroup.name.length > 20
        ? this.toolGroup.name.substring(0, 17) + '...'
        : this.toolGroup.name;

      const logoSrc = window.location.origin + '/img/sidebar-header-tolspor2.png';

      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Print QR Code</title>
            <style>
              @page {
                size: ${pageWidth} ${pageHeight};
                margin: 0;
              }
              body, html {
                margin: 0;
                padding: 0;
                width: ${pageWidth};
                height: ${pageHeight};
                overflow: hidden;
                display: flex;
                justify-content: center;
                align-items: center;
                box-sizing: border-box;
              }
              .qr-print-container {
                width: ${pageWidth};
                height: ${pageHeight};
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-items: center;
                background: #ffffff;
                box-sizing: border-box;
                padding: ${padding};
              }
              .print-logo {
                width: ${logoWidth};
                height: auto;
                max-height: ${logoHeight};
                margin-bottom: ${logoMarginBottom};
                display: block;
                object-fit: contain;
              }
              .qrcode-vue {
                width: ${qrSize};
                height: ${qrSize};
                max-width: ${qrSize};
                max-height: ${qrSize};
                object-fit: contain;
              }
              h3 {
                color: black;
                font-weight: bold;
                font-size: ${fontSize};
                margin: 0;
                text-align: center;
                max-width: calc(${pageWidth} - ${padding} * 2);
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
            </style>
          </head>
          <body>
            <div class="qr-print-container">
              <img src="${logoSrc}" alt="app-logo" class="print-logo"/>
              <svg class="qrcode-vue" width="${qrSize}" height="${qrSize}" viewBox="${this.$refs.printqrcode.$el.getAttribute('viewBox')}">${this.$refs.printqrcode.$el.innerHTML}</svg>
              <h3>${truncatedName}</h3>
            </div>
            <script>
              var img = document.querySelector('.print-logo');
              function doPrint() { window.focus(); window.print(); }
              if (img.complete) { doPrint(); } else { img.onload = doPrint; img.onerror = doPrint; }
              window.addEventListener('afterprint', function() { window.close(); });
            <` + `/script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  }
};
</script>

<style scoped>
/* Fill the column so the history card matches the info card's height (Card adds 20px margins). */
.stretch-card {
  height: calc(100% - 40px);
}
.qr-print-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
@media print {
  h3 {
    color: black !important;
    font-weight: bold !important;
  }
  .qr-print-container {
    width: 100%;
    height: 50vh;
    position: fixed;
    top: 0;
    left: 0;
  }
  .qrcode-vue {
    width: 100%;
    height: auto;
  }
  .print-logo {
    width: 256px;
    padding-bottom: 10px;
  }
}

.black {
  color: black;
}

.red {
  color: red;
}

.green {
  color: green;
}

.material-icons {
  cursor: pointer;
}

.nav-item {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.dropdown-menu {
  min-width: 100%;
  transform: translateX(0) !important;
}
.dropdown-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
}
.dropdown-item i {
  font-size: 1.25rem;
  margin-right: 0.5rem;
}

.dropdown-toggle::after {
  display: none !important;
}
</style>
